import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../api/client';

export const AdminDashboardPage = () => {
  const { logout, user } = useAuth();
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Users state
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRestrictModal, setShowRestrictModal] = useState(false);
  const [restrictReason, setRestrictReason] = useState('');
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [commissionForm, setCommissionForm] = useState({ loadInCommission: '', withdrawalCommission: '' });

  // Commission state
  const [commissionSettings, setCommissionSettings] = useState([]);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [showCommissionEditModal, setShowCommissionEditModal] = useState(false);
  const [editCommissionForm, setEditCommissionForm] = useState({ loadInCommission: '', withdrawalCommission: '' });

  // BondPay config state
  const [bondpayConfig, setBondpayConfig] = useState({ merchant_id: '', payin_api_key: '', payout_api_key: '' });
  const [showPasswords, setShowPasswords] = useState({ payin: false, payout: false });
  const [bondpaySaving, setBondpaySaving] = useState(false);
  const [bondpaySuccess, setBondpaySuccess] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes, commissionsRes, configRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers({ search: userSearch, status: userStatusFilter }),
        adminAPI.getCommissionSettingsAll(),
        adminAPI.getPaymentConfig().catch(() => ({ data: { data: null } }))
      ]);

      setStats(statsRes.data.data);
      setUsers(usersRes.data.data || []);
      setCommissionSettings(commissionsRes.data.data || []);
      if (configRes.data.data) {
        setBondpayConfig({
          merchant_id: configRes.data.data.merchant_id || '',
          payin_api_key: configRes.data.data.payin_api_key || '',
          payout_api_key: configRes.data.data.payout_api_key || '',
        });
      }
      setError('');
    } catch (err) {
      setError('Failed to load admin data');
      console.error('Error fetching admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchUsers = (e) => {
    e.preventDefault();
    fetchAllData();
  };

  const handleFreeze = async (userId) => {
    if (window.confirm('Are you sure you want to freeze this account?')) {
      try {
        await adminAPI.freezeUser(userId);
        fetchAllData();
        setError('');
      } catch (err) {
        setError('Failed to freeze account');
      }
    }
  };

  const handleActivate = async (userId) => {
    try {
      await adminAPI.activateUser(userId);
      fetchAllData();
      setError('');
    } catch (err) {
      setError('Failed to activate account');
    }
  };

  const handleRestrict = async () => {
    if (!restrictReason.trim()) {
      setError('Restriction reason is required');
      return;
    }
    try {
      await adminAPI.restrictUser(selectedUser.id, { reason: restrictReason });
      setShowRestrictModal(false);
      setRestrictReason('');
      fetchAllData();
      setError('');
    } catch (err) {
      setError('Failed to restrict account');
    }
  };

  const handleShowCommission = (user) => {
    setSelectedUser(user);
    setCommissionForm({ loadInCommission: '', withdrawalCommission: '' });
    setShowCommissionModal(true);
  };

  const handleUpdateCommission = async () => {
    if (!commissionForm.loadInCommission || !commissionForm.withdrawalCommission) {
      setError('Commission rates required');
      return;
    }
    try {
      await adminAPI.updateCommissionSettings({
        userId: selectedUser.id,
        loadInCommission: parseFloat(commissionForm.loadInCommission),
        withdrawalCommission: parseFloat(commissionForm.withdrawalCommission),
      });
      setShowCommissionModal(false);
      fetchAllData();
      setError('');
    } catch (err) {
      setError('Failed to update commission settings');
    }
  };

  const handleEditCommission = (setting) => {
    setSelectedSetting(setting);
    setEditCommissionForm({
      loadInCommission: setting.load_in_commission,
      withdrawalCommission: setting.withdrawal_commission,
    });
    setShowCommissionEditModal(true);
  };

  const handleSaveCommission = async () => {
    if (!editCommissionForm.loadInCommission || !editCommissionForm.withdrawalCommission) {
      setError('All commission rates are required');
      return;
    }
    try {
      await adminAPI.updateCommissionSettings({
        userId: selectedSetting.user_id,
        loadInCommission: parseFloat(editCommissionForm.loadInCommission),
        withdrawalCommission: parseFloat(editCommissionForm.withdrawalCommission),
      });
      setShowCommissionEditModal(false);
      fetchAllData();
      setError('');
    } catch (err) {
      setError('Failed to save commission settings');
    }
  };

  const handleBondpayChange = (e) => {
    const { name, value } = e.target;
    setBondpayConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveBondpay = async (e) => {
    e.preventDefault();
    if (!bondpayConfig.merchant_id.trim() || !bondpayConfig.payin_api_key.trim() || !bondpayConfig.payout_api_key.trim()) {
      setError('All fields are required');
      return;
    }
    setBondpaySaving(true);
    try {
      await adminAPI.updatePaymentConfig({
        merchant_id: bondpayConfig.merchant_id.trim(),
        payin_api_key: bondpayConfig.payin_api_key.trim(),
        payout_api_key: bondpayConfig.payout_api_key.trim(),
      });
      setBondpaySuccess('Configuration saved successfully! ✅');
      setTimeout(() => setBondpaySuccess(''), 5000);
      setError('');
    } catch (err) {
      setError('Failed to save configuration');
    } finally {
      setBondpaySaving(false);
    }
  };

  const menuItems = [
    { id: 'dashboard', icon: '📊', label: 'Dashboard' },
    { id: 'users', icon: '👥', label: 'Users' },
    { id: 'commission', icon: '⚙️', label: 'Commission' },
    { id: 'referrals', icon: '🎯', label: 'Referrals' },
    { id: 'payment-config', icon: '🔧', label: 'Payment' },
  ];

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-500/20 text-green-400',
      frozen: 'bg-blue-500/20 text-blue-400',
      restricted: 'bg-orange-500/20 text-orange-400',
    };
    return <span className={`px-2 py-1 rounded text-xs font-semibold ${colors[status] || 'bg-gray-500/20 text-gray-400'}`}>{status}</span>;
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'dashboard':
        return (
          <div className="text-white">
            <h3 className="text-2xl font-bold mb-6">Admin Dashboard</h3>
            {stats && (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-2">Total Users</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.totalUsers || 0}</p>
                </div>
                <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-2">Total Wallet Balance</p>
                  <p className="text-3xl font-bold text-yellow-600">₹{parseFloat(stats.totalWalletBalance || 0).toFixed(2)}</p>
                </div>
                <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-2">Pending Withdrawals</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.pendingWithdrawals || 0}</p>
                </div>
              </div>
            )}
          </div>
        );

      case 'users':
        return (
          <div className="text-white">
            <h3 className="text-2xl font-bold mb-6">Manage Users</h3>
            <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-6 mb-6">
              <form onSubmit={handleSearchUsers} className="flex gap-3 mb-4">
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search by username or email..."
                  className="flex-1 bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 focus:outline-none focus:border-yellow-600"
                />
                <select
                  value={userStatusFilter}
                  onChange={(e) => setUserStatusFilter(e.target.value)}
                  className="bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 focus:outline-none focus:border-yellow-600"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="frozen">Frozen</option>
                  <option value="restricted">Restricted</option>
                </select>
                <button type="submit" className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold px-6 py-2 rounded">Search</button>
              </form>
            </div>
            <div className="space-y-3">
              {users.length === 0 ? (
                <p className="text-gray-400">No users found</p>
              ) : (
                users.map(user => (
                  <div key={user.id} className="bg-slate-800/30 border border-slate-700 rounded-lg p-4 flex justify-between items-center">
                    <div className="flex-1">
                      <p className="font-semibold">{user.username}</p>
                      <p className="text-sm text-gray-400">{user.email}</p>
                      <p className="text-sm text-gray-400 mt-1">Balance: ₹{parseFloat(user.wallet_balance || 0).toFixed(2)}</p>
                      {getStatusBadge(user.account_status)}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleShowCommission(user)} className="text-blue-400 hover:underline text-sm" title="Edit commission">Commission</button>
                      {user.account_status === 'active' ? (
                        <>
                          <button onClick={() => handleFreeze(user.id)} className="text-orange-400 hover:underline text-sm">Freeze</button>
                          <button onClick={() => { setSelectedUser(user); setShowRestrictModal(true); }} className="text-red-400 hover:underline text-sm">Restrict</button>
                        </>
                      ) : (
                        <button onClick={() => handleActivate(user.id)} className="text-green-400 hover:underline text-sm">Activate</button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );

      case 'commission':
        return (
          <div className="text-white">
            <h3 className="text-2xl font-bold mb-6">Commission Settings</h3>
            <div className="space-y-4">
              {commissionSettings.length === 0 ? (
                <p className="text-gray-400">No commission settings found</p>
              ) : (
                commissionSettings.map(setting => (
                  <div key={setting.id} className="bg-slate-800/30 border border-slate-700 rounded-lg p-4 flex justify-between items-center">
                    <div>
                      <p className="font-semibold">{setting.user_id ? `User ID: ${setting.user_id} (Override)` : 'Global Default'}</p>
                      <p className="text-sm text-gray-400 mt-2">Load In: {setting.load_in_commission}% | Withdrawal: {setting.withdrawal_commission}%</p>
                    </div>
                    <button onClick={() => handleEditCommission(setting)} className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold px-4 py-2 rounded">Edit</button>
                  </div>
                ))
              )}
            </div>
          </div>
        );

      case 'referrals':
        return (
          <div className="text-white">
            <h3 className="text-2xl font-bold mb-6">Referral Management</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-4 text-center">
                <p className="text-gray-400 text-sm mb-2">Total Referrals</p>
                <p className="text-3xl font-bold text-yellow-600">{stats?.totalReferrals || 0}</p>
              </div>
              <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-4 text-center">
                <p className="text-gray-400 text-sm mb-2">Active</p>
                <p className="text-3xl font-bold text-yellow-600">{stats?.activeReferrals || 0}</p>
              </div>
              <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-4 text-center">
                <p className="text-gray-400 text-sm mb-2">Total Earnings</p>
                <p className="text-3xl font-bold text-yellow-600">₹{parseFloat(stats?.totalReferralEarnings || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
        );

      case 'payment-config':
        return (
          <div className="text-white">
            <h3 className="text-2xl font-bold mb-6">BondPay Configuration</h3>
            {bondpaySuccess && <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded mb-4">{bondpaySuccess}</div>}
            <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-6 max-w-2xl">
              <form onSubmit={handleSaveBondpay} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Merchant ID</label>
                  <input type="text" name="merchant_id" value={bondpayConfig.merchant_id} onChange={handleBondpayChange} placeholder="Enter Merchant ID" className="w-full bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 focus:outline-none focus:border-yellow-600" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Payin API Key</label>
                  <div className="flex gap-2">
                    <input type={showPasswords.payin ? 'text' : 'password'} name="payin_api_key" value={bondpayConfig.payin_api_key} onChange={handleBondpayChange} placeholder="Enter Payin API Key" className="flex-1 bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 focus:outline-none focus:border-yellow-600" />
                    <button type="button" onClick={() => setShowPasswords(prev => ({ ...prev, payin: !prev.payin }))} className="text-gray-400 hover:text-white">{showPasswords.payin ? '👁️' : '🙈'}</button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Payout API Key</label>
                  <div className="flex gap-2">
                    <input type={showPasswords.payout ? 'text' : 'password'} name="payout_api_key" value={bondpayConfig.payout_api_key} onChange={handleBondpayChange} placeholder="Enter Payout API Key" className="flex-1 bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 focus:outline-none focus:border-yellow-600" />
                    <button type="button" onClick={() => setShowPasswords(prev => ({ ...prev, payout: !prev.payout }))} className="text-gray-400 hover:text-white">{showPasswords.payout ? '👁️' : '🙈'}</button>
                  </div>
                </div>
                <button type="submit" disabled={bondpaySaving} className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded transition disabled:opacity-50">
                  {bondpaySaving ? 'Saving...' : 'Update Configuration'}
                </button>
              </form>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-900">
      {/* Header */}
      <nav className="bg-slate-800 border-b border-slate-700 shadow-lg sticky top-0 z-50">
        <div className="px-6 py-2 flex items-center justify-between">
          <img src="/logo.svg" alt="PaySolutions" className="h-20 w-auto flex-shrink-0 object-contain" />
          <button onClick={logout} className="flex-shrink-0 text-2xl hover:text-red-400 transition-all duration-200 hover:scale-110" title="Logout">⬅️</button>
        </div>
      </nav>

      {/* Main Layout */}
      <div className="flex h-[calc(100vh-96px)]">
        {/* Left Sidebar - Menu Icons */}
        <div className="flex flex-col items-center justify-center gap-6 py-6 px-3 ml-4">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveMenu(item.id)}
              className="transition-all duration-200 group"
              title={item.label}
            >
              <div
                className={`text-2xl p-2 rounded-lg transition-all duration-200 ${
                  activeMenu === item.id
                    ? 'bg-yellow-600 text-white shadow-lg shadow-yellow-600/30'
                    : 'bg-yellow-600 text-white group-hover:bg-white group-hover:text-yellow-600'
                }`}
              >
                {item.icon}
              </div>
            </button>
          ))}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto p-8 pr-4 pl-6">
          {error && <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-6">{error}</div>}
          {renderContent()}
        </div>

        {/* Right Sidebar - Stats */}
        <div className="w-1/5 bg-slate-800/50 border-l border-slate-700 overflow-y-auto p-6 sticky top-14">
          <h3 className="text-white font-bold mb-4">📊 Stats</h3>
          {stats && (
            <div className="space-y-3">
              <div className="bg-slate-700/50 p-3 rounded-lg">
                <p className="text-gray-400 text-xs">Total Users</p>
                <p className="text-yellow-600 font-bold">{stats.totalUsers || 0}</p>
              </div>
              <div className="bg-slate-700/50 p-3 rounded-lg">
                <p className="text-gray-400 text-xs">Active Users</p>
                <p className="text-yellow-600 font-bold">{stats.activeUsers || 0}</p>
              </div>
              <div className="bg-slate-700/50 p-3 rounded-lg">
                <p className="text-gray-400 text-xs">Total Balance</p>
                <p className="text-yellow-600 font-bold">₹{parseFloat(stats.totalWalletBalance || 0).toFixed(2)}</p>
              </div>
              <div className="bg-slate-700/50 p-3 rounded-lg">
                <p className="text-gray-400 text-xs">Transactions</p>
                <p className="text-yellow-600 font-bold">{stats.totalTransactions || 0}</p>
              </div>
              <div className="bg-slate-700/50 p-3 rounded-lg">
                <p className="text-gray-400 text-xs">Pending</p>
                <p className="text-yellow-600 font-bold">{stats.pendingWithdrawals || 0}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Restrict Modal */}
      {showRestrictModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-white mb-4">Restrict Account: {selectedUser.username}</h2>
            <textarea value={restrictReason} onChange={(e) => setRestrictReason(e.target.value)} placeholder="Enter reason..." className="w-full bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 mb-4 focus:outline-none focus:border-yellow-600" rows="4" />
            <div className="flex gap-2">
              <button onClick={() => setShowRestrictModal(false)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded">Cancel</button>
              <button onClick={handleRestrict} className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded">Restrict</button>
            </div>
          </div>
        </div>
      )}

      {/* Commission Modal */}
      {showCommissionModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-white mb-4">Set Commission: {selectedUser.username}</h2>
            <input type="number" value={commissionForm.loadInCommission} onChange={(e) => setCommissionForm(prev => ({ ...prev, loadInCommission: e.target.value }))} placeholder="Load In (%)" className="w-full bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 mb-3 focus:outline-none focus:border-yellow-600" />
            <input type="number" value={commissionForm.withdrawalCommission} onChange={(e) => setCommissionForm(prev => ({ ...prev, withdrawalCommission: e.target.value }))} placeholder="Withdrawal (%)" className="w-full bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 mb-4 focus:outline-none focus:border-yellow-600" />
            <div className="flex gap-2">
              <button onClick={() => setShowCommissionModal(false)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded">Cancel</button>
              <button onClick={handleUpdateCommission} className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Commission Settings Modal */}
      {showCommissionEditModal && selectedSetting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-white mb-4">Edit Commission</h2>
            <input type="number" value={editCommissionForm.loadInCommission} onChange={(e) => setEditCommissionForm(prev => ({ ...prev, loadInCommission: e.target.value }))} placeholder="Load In (%)" className="w-full bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 mb-3 focus:outline-none focus:border-yellow-600" step="0.1" />
            <input type="number" value={editCommissionForm.withdrawalCommission} onChange={(e) => setEditCommissionForm(prev => ({ ...prev, withdrawalCommission: e.target.value }))} placeholder="Withdrawal (%)" className="w-full bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 mb-4 focus:outline-none focus:border-yellow-600" step="0.1" />
            <div className="flex gap-2">
              <button onClick={() => setShowCommissionEditModal(false)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded">Cancel</button>
              <button onClick={handleSaveCommission} className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
